// src/features/hotels/components/notifications/HotelNotifications.tsx

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDataProvider, useNotify, useTranslate } from 'react-admin';
import {
    Box,
    Typography,
    Button,
    Alert,
    CircularProgress,
    TextField,
    Chip,
    IconButton,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Paper,
    List,
    ListItem,
    ListItemText,
    ListItemSecondaryAction,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    InputAdornment,
    Divider,
} from '@mui/material';
import {
    Notifications as NotificationsIcon,
    Save as SaveIcon,
    Add as AddIcon,
    Delete as DeleteIcon,
    Warning as WarningIcon,
    Key as KeyIcon,
    Email as EmailIcon,
    CheckCircle as CheckCircleIcon,
    ErrorOutline as ErrorOutlineIcon,
    Refresh as RefreshIcon,
} from '@mui/icons-material';

// COMPONENTES DEL SISTEMA
import { MyFormContainer } from '../../../../components/forms/MyFormContainer';
import { 
    MyFormLayout, 
    FormCard, 
    MyFormSection 
} from '../../../../components/forms/MyFormLayout';

import { useHotelPermissions } from '../../hooks/useHotelPermissions';
import { Hotel, NotifierConfiguration, NotifierTemplate } from '../../types';

const HotelNotifications: React.FC = () => {
    const { id: hotelId } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const dataProvider = useDataProvider() as any;
    const notify = useNotify();
    const translate = useTranslate();

    // Severity levels with translations
    const SEVERITY_LEVELS = [
        { value: 'DEBUG', label: translate('resources.hotels.alert_config.debug'), color: 'default' as const },
        { value: 'INFO', label: translate('resources.hotels.alert_config.info'), color: 'info' as const },
        { value: 'WARNING', label: translate('resources.hotels.alert_config.warning'), color: 'warning' as const },
        { value: 'ERROR', label: translate('resources.hotels.alert_config.error'), color: 'error' as const },
        { value: 'CRITICAL', label: translate('resources.hotels.alert_config.critical'), color: 'error' as const },
    ];

    // Estados
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [hotel, setHotel] = useState<Hotel | null>(null);
    const [alertRecipients, setAlertRecipients] = useState<string[]>([]);
    const [minSeverityToNotify, setMinSeverityToNotify] = useState<string>('WARNING');
    const [newEmail, setNewEmail] = useState('');
    const [emailError, setEmailError] = useState('');
    const [unsavedChanges, setUnsavedChanges] = useState(false);
    const [exitDialogOpen, setExitDialogOpen] = useState(false);
    const [notifierConfig, setNotifierConfig] = useState<NotifierConfiguration | null>(null);
    const [templates, setTemplates] = useState<NotifierTemplate[]>([]);
    const [selectedTemplateId, setSelectedTemplateId] = useState<string>('');
    const [apiKey, setApiKey] = useState<string>('');
    const [accountName, setAccountName] = useState<string>('');
    const [loadingTemplates, setLoadingTemplates] = useState(false);
const [connectionStatus, setConnectionStatus] = useState<'untested' | 'connected' | 'failed'>('untested');
const [testingConnection, setTestingConnection] = useState(false);

     const [originalNotifierConfig, setOriginalNotifierConfig] = useState<NotifierConfiguration | null>(null);

    // Estados originales para detectar cambios
    const [originalRecipients, setOriginalRecipients] = useState<string[]>([]);
    const [originalSeverity, setOriginalSeverity] = useState<string>('WARNING');

    const permissions = useHotelPermissions(hotel);

    // Cargar datos
    useEffect(() => {
        if (hotelId) {
            loadData();
        }
    }, [hotelId]);

    // Detectar cambios no guardados
      useEffect(() => {
        const hasAlertChanges = 
            JSON.stringify(alertRecipients) !== JSON.stringify(originalRecipients) ||
            minSeverityToNotify !== originalSeverity;
        
        const hasNotifierChanges = 
            apiKey !== '' || 
            accountName !== (originalNotifierConfig?.accountName || '') ||
            selectedTemplateId !== (originalNotifierConfig?.selectedTemplateId || '');
            
        setUnsavedChanges(hasAlertChanges || hasNotifierChanges);
    }, [alertRecipients, minSeverityToNotify, originalRecipients, originalSeverity, 
        apiKey, accountName, selectedTemplateId, originalNotifierConfig]);

    const loadData = async () => {
        if (!hotelId) return;
        setLoading(true);
        
        try {
            const [hotelResponse, notificationResponse, notifierResponse] = await Promise.all([
                dataProvider.getOne('hotels', { id: hotelId }),
                dataProvider.getNotificationSettings(hotelId),
                dataProvider.getNotifierConfiguration(hotelId)
            ]);

            setHotel(hotelResponse.data);
            
            // Configuración de alertas existente
            const settings = notificationResponse.data;
            setAlertRecipients(settings.alertRecipients || []);
            setMinSeverityToNotify(settings.minSeverityToNotify || 'WARNING');
            setOriginalRecipients(settings.alertRecipients || []);
            setOriginalSeverity(settings.minSeverityToNotify || 'WARNING');
            
            // Nueva configuración del Notifier
            const notifierData = notifierResponse.data;
            setNotifierConfig(notifierData);
            setOriginalNotifierConfig(notifierData);
            setSelectedTemplateId(notifierData.selectedTemplateId || '');
            setAccountName(notifierData.accountName || '');
            
            // Si está configurado, cargar templates
            if (notifierData.isConfigured) {
                await loadTemplates();
            }

        } catch (error) {
            console.error('Error loading settings:', error);
            notify(translate('resources.hotels.alert_config.load_error'), { type: 'error' });
        } finally {
            setLoading(false);
        }
    };

     const loadTemplates = async () => {
        if (!hotelId) return;
        setLoadingTemplates(true);
        
        try {
            const response = await dataProvider.getNotifierTemplates(hotelId);
            setTemplates(response.data || []);
        } catch (error) {
            console.error('Error loading templates:', error);
        } finally {
            setLoadingTemplates(false);
        }
    };

    const handleTestConnection = async () => {
    if (!hotelId) return;
    
    setTestingConnection(true);
    try {
        const result = await dataProvider.testNotifierConnection(hotelId);
        
        if (result.data.connected) {
            setConnectionStatus('connected');
            notify(translate('resources.hotels.alert_config.connection_success'), { type: 'success' });
        } else {
            setConnectionStatus('failed');
            notify(translate('resources.hotels.alert_config.connection_failed'), { type: 'warning' });
        }
    } catch (error) {
        setConnectionStatus('failed');
        notify(translate('resources.hotels.alert_config.connection_test_error'), { type: 'error' });
    } finally {
        setTestingConnection(false);
    }
};

 // Método handleSaveApiKey simplificado:
const handleSaveApiKey = async () => {
    if (!hotelId || !apiKey) return;
    
    try {
        // Siempre guardar con force: true
        await dataProvider.updateNotifierApiKey(hotelId, {
            apiKey,
            accountName: accountName || undefined,
            force: true // Siempre forzar guardado
        });
        
        notify(translate('resources.hotels.alert_config.api_key_saved'), { type: 'success' });
        
        // Después de guardar, intentar probar la conexión automáticamente
        setTestingConnection(true);
        try {
            const testResult = await dataProvider.testNotifierConnection(hotelId);
            
            if (testResult.data.connected) {
                setConnectionStatus('connected');
                notify(translate('resources.hotels.alert_config.connection_verified'), { type: 'success' });
            } else {
                setConnectionStatus('failed');
                notify(translate('resources.hotels.alert_config.api_key_saved_no_connection'), { type: 'warning' });
            }
        } catch (testError) {
            setConnectionStatus('failed');
            // No mostrar otro error, ya notificamos que se guardó
        } finally {
            setTestingConnection(false);
        }
        
        // Recargar configuración
        await loadData();
        setApiKey(''); // Limpiar el campo de API key
        
    } catch (error: any) {
                    notify(translate('resources.hotels.alert_config.api_key_save_error'), { type: 'error' });
    }
};


     const handleSaveTemplate = async () => {
        if (!hotelId || !selectedTemplateId) return;
        
        try {
            await dataProvider.updateNotifierTemplate(hotelId, {
                templateId: selectedTemplateId,
                defaultLanguage: notifierConfig?.defaultLanguage
            });
            
            notify(translate('resources.hotels.alert_config.template_updated'), { type: 'success' });
            setOriginalNotifierConfig({
                ...notifierConfig!,
                selectedTemplateId
            });
            
        } catch (error) {
            notify(translate('resources.hotels.alert_config.template_update_error'), { type: 'error' });
        }
    };

    const validateEmail = (email: string): boolean => {
        const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return re.test(email);
    };

    const handleAddEmail = () => {
        const trimmedEmail = newEmail.trim();
        
        if (!trimmedEmail) {
            setEmailError(translate('resources.hotels.alert_config.email_required'));
            return;
        }
        
        if (!validateEmail(trimmedEmail)) {
            setEmailError(translate('resources.hotels.alert_config.email_invalid'));
            return;
        }
        
        if (alertRecipients.includes(trimmedEmail)) {
            setEmailError(translate('resources.hotels.alert_config.email_exists'));
            return;
        }
        
        setAlertRecipients([...alertRecipients, trimmedEmail]);
        setNewEmail('');
        setEmailError('');
    };

    const handleRemoveEmail = (email: string) => {
        setAlertRecipients(alertRecipients.filter(e => e !== email));
    };

      const handleSave = async () => {
        if (!hotelId || !permissions.canEdit) return;
        
        setSaving(true);
        try {
            // Guardar configuración de alertas
            await dataProvider.updateNotificationSettings(hotelId, {
                alertRecipients,
                minSeverityToNotify
            });
            
            // Si hay cambios en el template, guardarlos
            if (selectedTemplateId !== originalNotifierConfig?.selectedTemplateId) {
                await handleSaveTemplate();
            }
            
            // Actualizar valores originales
            setOriginalRecipients([...alertRecipients]);
            setOriginalSeverity(minSeverityToNotify);
            
            notify('Configuración guardada con éxito', { type: 'success' });
            
        } catch (error) {
            console.error('Error saving:', error);
            notify('Error al guardar la configuración', { type: 'error' });
        } finally {
            setSaving(false);
        }
    };

    const handleExitConfirm = () => {
        setExitDialogOpen(false);
        navigate(`/hotels/${hotelId}`);
    };

    if (loading) {
        return (
            <MyFormContainer>
                <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
                    <CircularProgress />
                </Box>
            </MyFormContainer>
        );
    }

    if (!hotel) {
        return (
            <MyFormContainer>
                <Alert severity="error">
                    {translate('resources.hotels.alert_config.hotel_load_error')}
                </Alert>
            </MyFormContainer>
        );
    }

    return (
        <MyFormContainer>
            <MyFormLayout spacing={3} maxColumns={1}>
                
                {/* Configuración de Alertas */}
                <FormCard xs={12}>
                    <MyFormSection
                        variant='outlined'
                        title={translate('resources.hotels.alert_config.title')}
                        subtitle={translate('resources.hotels.alert_config.subtitle')}
                        icon={<NotificationsIcon />}
                    >
                        {/* Nivel de Severidad */}
                        <Box sx={{ mb: 3 }}>
                            <FormControl fullWidth>
                                <InputLabel>{translate('resources.hotels.alert_config.min_severity_label')}</InputLabel>
                                <Select
                                    value={minSeverityToNotify}
                                    label={translate('resources.hotels.alert_config.min_severity_label')}
                                    onChange={(e) => setMinSeverityToNotify(e.target.value)}
                                    disabled={!permissions.canEdit}
                                >
                                    {SEVERITY_LEVELS.map((level) => (
                                        <MenuItem key={level.value} value={level.value}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Chip
                                                    label={level.label}
                                                    color={level.color}
                                                    size="small"
                                                />
                                            </Box>
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                                {translate('resources.hotels.alert_config.min_severity_help')}
                            </Typography>
                        </Box>

                        {/* Lista de Destinatarios */}
                        <Box>
                            <Typography variant="subtitle2" gutterBottom>
                                {translate('resources.hotels.alert_config.recipients_title')}
                            </Typography>
                            
                            {/* Agregar nuevo email */}
                            {permissions.canEdit && (
                                <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                                    <TextField
                                        fullWidth
                                        label={translate('resources.hotels.alert_config.email_label')}
                                        value={newEmail}
                                        onChange={(e) => {
                                            setNewEmail(e.target.value);
                                            setEmailError('');
                                        }}
                                        error={!!emailError}
                                        helperText={emailError}
                                        onKeyPress={(e) => {
                                            if (e.key === 'Enter') {
                                                handleAddEmail();
                                            }
                                        }}
                                    />
                                    <Button
                                        variant="contained"
                                        onClick={handleAddEmail}
                                        startIcon={<AddIcon />}
                                        sx={{ minWidth: 120 }}
                                    >
                                        {translate('resources.hotels.alert_config.add')}
                                    </Button>
                                </Box>
                            )}

                            {/* Lista de emails */}
                            {alertRecipients.length === 0 ? (
                                <Paper sx={{ p: 2, textAlign: 'center' }}>
                                    <Typography color="text.secondary">
                                        {translate('resources.hotels.alert_config.no_recipients')}
                                    </Typography>
                                </Paper>
                            ) : (
                                <Paper variant="outlined">
                                    <List>
                                        {alertRecipients.map((email, index) => (
                                            <ListItem key={index} divider={index < alertRecipients.length - 1}>
                                                <ListItemText primary={email} />
                                                {permissions.canEdit && (
                                                    <ListItemSecondaryAction>
                                                        <IconButton
                                                            edge="end"
                                                            onClick={() => handleRemoveEmail(email)}
                                                            color="error"
                                                        >
                                                            <DeleteIcon />
                                                        </IconButton>
                                                    </ListItemSecondaryAction>
                                                )}
                                            </ListItem>
                                        ))}
                                    </List>
                                </Paper>
                            )}
                        </Box>

                        {/* Botón Guardar */}
                        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                            <Button
                                variant="contained"
                                startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                                onClick={handleSave}
                                disabled={saving || !unsavedChanges || !permissions.canEdit}
                            >
                                {saving ? translate('resources.hotels.alert_config.saving') : translate('resources.hotels.alert_config.save_config')}
                            </Button>
                        </Box>
                    </MyFormSection>
                </FormCard>

                <FormCard xs={12} >
                    <MyFormSection
                        variant='outlined'
                        title={translate('resources.hotels.alert_config.email_service_title')}
                        subtitle={translate('resources.hotels.alert_config.email_service_subtitle')}
                        icon={<EmailIcon />}
                    >
                       
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                           
                                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                        {notifierConfig?.isConfigured && (
                                            <>
                                                {connectionStatus === 'connected' && (
                                                    <Chip 
                                                        icon={<CheckCircleIcon />} 
                                                        label={translate('resources.hotels.alert_config.connected')} 
                                                        color="success" 
                                                        size="small" 
                                                    />
                                                )}
                                                {connectionStatus === 'failed' && (
                                                    <Chip 
                                                        icon={<ErrorOutlineIcon />} 
                                                        label={translate('resources.hotels.alert_config.no_connection')} 
                                                        color="warning" 
                                                        size="small" 
                                                    />
                                                )}
                                                {connectionStatus === 'untested' && (
                                                    <Chip 
                                                        label={translate('resources.hotels.alert_config.untested')} 
                                                        color="default" 
                                                        size="small" 
                                                    />
                                                )}
                                            </>
                                        )}
                                    </Box>
                                </Box>
                           
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
                                <TextField
                                    fullWidth
                                    label={translate('resources.hotels.alert_config.api_key_label')}
                                    type="password"
                                    value={apiKey}
                                    onChange={(e) => setApiKey(e.target.value)}
                                    disabled={!permissions.canEdit}
                                    placeholder={notifierConfig?.isConfigured ? translate('resources.hotels.alert_config.api_key_current_placeholder') : translate('resources.hotels.alert_config.api_key_placeholder')}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <KeyIcon />
                                            </InputAdornment>
                                        ),
                                    }}
                                    helperText={notifierConfig?.isConfigured 
                                        ? translate('resources.hotels.alert_config.api_key_keep_help') 
                                        : translate('resources.hotels.alert_config.api_key_required_help')}
                                />
                                
                                <TextField
                                    fullWidth
                                    label={translate('resources.hotels.alert_config.account_name_label')}
                                    value={accountName}
                                    onChange={(e) => setAccountName(e.target.value)}
                                    disabled={!permissions.canEdit}
                                    placeholder={translate('resources.hotels.alert_config.account_name_placeholder')}
                                 
                                />
                                
                                <Box sx={{ display: 'flex', gap: 2 }}>
                                    <Button
                                        variant="contained"
                                        onClick={handleSaveApiKey}
                                        disabled={!apiKey || !permissions.canEdit}
                                        startIcon={<SaveIcon />}
                                    >
                                        {notifierConfig?.isConfigured ? translate('resources.hotels.alert_config.update_api_key') : translate('resources.hotels.alert_config.config_api_key')}
                                    </Button>
                                    
                                    {/* Botón para probar conexión - Solo visible si está configurado */}
                                    {notifierConfig?.isConfigured && (
                                        <Button
                                            variant="outlined"
                                            onClick={handleTestConnection}
                                            disabled={testingConnection || !permissions.canEdit}
                                            startIcon={testingConnection ? 
                                                <CircularProgress size={20} /> : 
                                                <RefreshIcon />
                                            }
                                        >
                                            {testingConnection ? translate('resources.hotels.alert_config.testing') : translate('resources.hotels.alert_config.test_connection')}
                                        </Button>
                                    )}
                                </Box>
            
            {/* Mensaje de estado de conexión */}
            {connectionStatus === 'failed' && notifierConfig?.isConfigured && (
                <Alert severity="warning">
                    {translate('resources.hotels.alert_config.connection_warning')}
                </Alert>
            )}
        </Box>
   

                        {/* Selector de plantillas - Solo si está configurado */}
                        {notifierConfig?.isConfigured && (
                            <Box sx={{ mt: 3 }}>
                                <Divider sx={{ mb: 2 }} />
                                <Typography variant="subtitle1" gutterBottom>
                                    {translate('resources.hotels.alert_config.template_title')}
                                </Typography>
                                
                                <FormControl fullWidth sx={{ mt: 2 }}>
                                    <InputLabel>{translate('resources.hotels.alert_config.template_label')}</InputLabel>
                                    <Select
                                        value={selectedTemplateId}
                                        label={translate('resources.hotels.alert_config.template_label')}
                                        onChange={(e) => setSelectedTemplateId(e.target.value)}
                                        disabled={!permissions.canEdit || loadingTemplates}
                                    >
                                        <MenuItem value="">
                                            {loadingTemplates ? translate('resources.hotels.alert_config.loading') : translate('resources.hotels.alert_config.template_placeholder')}
                                        </MenuItem>
                                        {templates.map((template) => (
                                            <MenuItem key={template.id} value={template.id}>
                                                {template.name} ({template.language})
                                            </MenuItem>
                                        ))}
                                    </Select>
                                  
                                </FormControl>

                              
                            </Box>
                        )}
                    </MyFormSection>
                </FormCard>


            </MyFormLayout>

            {/* Diálogo de confirmación para salir */}
            <Dialog
                open={exitDialogOpen}
                onClose={() => setExitDialogOpen(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 1,
                    borderBottom: '1px solid #e5e7eb',
                    pb: 2
                }}>
                    <WarningIcon sx={{ color: '#f59e0b' }} />
                    {translate('resources.hotels.alert_config.unsaved_changes_title')}
                </DialogTitle>
                <DialogContent sx={{ pt: 3 }}>
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        {translate('resources.hotels.alert_config.unsaved_changes_message')}
                    </Alert>
                    <DialogContentText>
                        {translate('resources.hotels.alert_config.unsaved_changes_question')}
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ p: 2.5, gap: 1 }}>
                    <Button 
                        onClick={() => setExitDialogOpen(false)}
                        variant="outlined"
                    >
                        {translate('resources.hotels.alert_config.continue_editing')}
                    </Button>
                    <Button 
                        onClick={handleExitConfirm}
                        variant="contained"
                        color="warning"
                    >
                        {translate('resources.hotels.alert_config.exit_without_saving')}
                    </Button>
                </DialogActions>
            </Dialog>
        </MyFormContainer>
    );
};

export default HotelNotifications;